<script setup lang="ts">
import type { Form } from '#ui/types';
import { z } from 'zod';

import { apiAccountExist, BackendErrorCode } from '~/apiClient';

interface LoginFormProps {
    email?: string;
}

// PROPS
const { email } = defineProps<LoginFormProps>();

// EVENTS
const emit = defineEmits<{
    success: [boolean];
    notFound: [string];
    notVerified: [string];
}>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { isLgOrGreater } = useResponsive();
const { login } = useAuthUtils();
const { openRequestPasswordResetModal } = useModals();
const { notifyBackendError, notifyGenericError } = useNotification();
const { trackLoginSuccess } = useTracking();

// DATA
const formSchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
    password: z
        .string()
        .refine(password => !isPromptingPassword.value || !!password, `${tt('validation.msg.required', { field: tt('login.password') })}`),
});

type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;

const formData = reactive<FormData>({
    email: email ?? '',
    password: '',
});

const form = useTemplateRef<Form<FormSchema>>('form');
const isSubmitting = ref(false);
const isPromptingPassword = ref(false);

// COMPUTED
const submitLabel = computed(() => tt(isPromptingPassword.value ? 'login.loginButton' : 'login.nextButton'));

// METHODS
const accountCheck = (): Promise<void> =>
    apiAccountExist({ email: formData.email })
        .then(() => {
            isPromptingPassword.value = true;
        })
        .catch(error => {
            if (isFetchError(error) && isBackendError(error.data) && error.data.error.code === BackendErrorCode.EntityNotFound) {
                emit('notFound', formData.email);
            } else {
                return Promise.reject(error);
            }
        });

const accountLogin = (): Promise<void | Record<string, any> | undefined> =>
    login(formData.email, formData.password)
        .then(() => {
            trackLoginSuccess();
            emit('success', true);
        })
        .catch(error => {
            if (isFetchError(error) && isBackendError(error.data)) {
                if (error.data.error.code === BackendErrorCode.AuthWrongPassword) {
                    form.value?.setErrors([{ name: 'password', message: tt('error.backend.AuthWrongPassword') }]);

                    return;
                }

                if (error.data?.error?.code === BackendErrorCode.AuthEmailNotVerified) {
                    emit('notVerified', formData.email);

                    return;
                }
            }

            return Promise.reject(error);
        });

const onSubmit = async (): Promise<void> => {
    isSubmitting.value = true;

    try {
        await (isPromptingPassword.value ? accountLogin : accountCheck)();
    } catch (error) {
        if (isFetchError(error) && isBackendError(error.data)) {
            notifyBackendError({ error: error.data.error });
        } else {
            notifyGenericError();
        }
    }

    isSubmitting.value = false;
};

const onForgotPasswordClick = (): void => {
    openRequestPasswordResetModal({ email: formData.email });
};

// LIFECYCLE
onMounted(() => {
    if (email) accountCheck();
});
</script>

<template>
    <div class="flex flex-col gap-6">
        <UForm
            ref="form"
            class="flex flex-col justify-center gap-6"
            :state="formData"
            :schema="formSchema"
            :validate-on="['input', 'change']"
            @submit="onSubmit"
        >
            <p class="text-muted text-center font-medium">{{ tt('login.subtitle') }}</p>

            <template v-if="!isPromptingPassword">
                <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                    <UInput
                        v-model.trim="formData.email"
                        autocomplete="username"
                        type="email"
                        inputmode="email"
                        :disabled="isSubmitting"
                        :autofocus="isLgOrGreater"
                    />
                </UFormField>
            </template>

            <template v-else>
                <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                    <UInput :model-value="formData.email" disabled :ui="{ base: 'disabled:bg-neutral-100' }">
                        <template #trailing>
                            <span class="text-primary cursor-pointer text-sm font-bold" @click="isPromptingPassword = false">{{
                                tt('login.change')
                            }}</span>
                        </template>
                    </UInput>
                </UFormField>

                <UFormField v-if="isPromptingPassword" class="w-full" :label="tt('login.password')" name="password" required>
                    <BasePasswordInput v-model="formData.password" autocomplete="current-password" :disabled="isSubmitting" />
                </UFormField>

                <button v-if="isPromptingPassword" type="button" class="mx-auto text-base font-bold underline" @click.prevent="onForgotPasswordClick">
                    {{ tt('login.forgotPassword') }}
                </button>
            </template>

            <UButton block :label="submitLabel" type="submit" size="xl" :loading="isSubmitting" />
        </UForm>
    </div>
</template>
